/**
 * TTS 服务 - 支持多音色语音合成
 * 使用 Web Speech API，兼容所有主流浏览器
 */

// 可用音色配置
export const AVAILABLE_VOICES = {
  // 美式英语
  'en-US-Female-1': { name: 'Samantha', gender: 'Female', locale: 'en-US', description: '美式女声 - 自然亲切', keywords: ['samantha', 'zira', 'jenny', 'aria', 'female', 'woman'] },
  'en-US-Female-2': { name: 'Victoria', gender: 'Female', locale: 'en-US', description: '美式女声 - 专业清晰', keywords: ['victoria', 'susan', 'linda', 'female'] },
  'en-US-Male-1': { name: 'Alex', gender: 'Male', locale: 'en-US', description: '美式男声 - 沉稳大气', keywords: ['alex', 'david', 'mark', 'guy', 'male', 'man'] },
  'en-US-Male-2': { name: 'Fred', gender: 'Male', locale: 'en-US', description: '美式男声 - 友好随和', keywords: ['fred', 'tom', 'james', 'male'] },
  // 英式英语
  'en-GB-Female': { name: 'Kate', gender: 'Female', locale: 'en-GB', description: '英式女声 - 优雅标准', keywords: ['kate', 'hazel', 'sonia', 'british', 'uk'] },
  'en-GB-Male': { name: 'Daniel', gender: 'Male', locale: 'en-GB', description: '英式男声 - 绅士风度', keywords: ['daniel', 'oliver', 'ryan', 'british', 'uk'] },
  // 澳洲英语
  'en-AU-Female': { name: 'Karen', gender: 'Female', locale: 'en-AU', description: '澳洲女声 - 阳光开朗', keywords: ['karen', 'natasha', 'australian', 'australia'] },
  'en-AU-Male': { name: 'Lee', gender: 'Male', locale: 'en-AU', description: '澳洲男声 - 热情友好', keywords: ['lee', 'william', 'australian', 'australia'] },
};

// 默认音色
export const DEFAULT_VOICE = 'en-US-Female-1';

// 当前选择的音色
let currentVoice = localStorage.getItem('tts_voice') || DEFAULT_VOICE;

// 缓存的系统音色
let systemVoicesCache = null;
let voicesInitialized = false;

/**
 * 获取并缓存系统音色列表
 */
function getSystemVoices() {
  if (!window.speechSynthesis) return [];
  
  // 强制刷新音色列表
  const voices = window.speechSynthesis.getVoices();
  if (voices.length > 0) {
    systemVoicesCache = voices;
    voicesInitialized = true;
  }
  
  return systemVoicesCache || [];
}

/**
 * 初始化音色列表
 */
export function initVoices() {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) {
      resolve([]);
      return;
    }
    
    // 尝试直接获取
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      systemVoicesCache = voices;
      voicesInitialized = true;
      console.log('TTS: Found', voices.length, 'voices');
      resolve(voices);
      return;
    }
    
    // 等待 voiceschanged 事件
    const handler = () => {
      systemVoicesCache = window.speechSynthesis.getVoices();
      voicesInitialized = true;
      console.log('TTS: Loaded', systemVoicesCache.length, 'voices');
      resolve(systemVoicesCache);
    };
    
    window.speechSynthesis.onvoiceschanged = handler;
    
    // 超时兜底
    setTimeout(() => {
      if (!voicesInitialized) {
        systemVoicesCache = window.speechSynthesis.getVoices();
        voicesInitialized = true;
        console.log('TTS: Timeout, found', systemVoicesCache?.length || 0, 'voices');
        resolve(systemVoicesCache || []);
      }
    }, 2000);
  });
}

/**
 * 查找最佳匹配的系统音色
 */
function findBestVoice(voiceKey) {
  // 确保音色列表已加载
  const voices = getSystemVoices();
  const voiceConfig = AVAILABLE_VOICES[voiceKey];
  
  if (!voiceConfig || !voices || voices.length === 0) {
    console.log('TTS: No voices available or invalid config');
    return null;
  }
  
  const { locale, gender, keywords } = voiceConfig;
  
  // 按优先级筛选
  // 1. 精确匹配语言区域
  let candidates = voices.filter(v => v.lang === locale || v.lang.replace('_', '-') === locale);
  
  // 2. 如果没有精确匹配，尝试语言前缀匹配
  if (candidates.length === 0) {
    const langPrefix = locale.split('-')[0];
    candidates = voices.filter(v => v.lang.startsWith(langPrefix));
  }
  
  // 3. 如果还是没有，使用所有英语音色
  if (candidates.length === 0) {
    candidates = voices.filter(v => v.lang.startsWith('en'));
  }
  
  // 4. 最后兜底：使用所有音色
  if (candidates.length === 0) {
    candidates = voices;
  }
  
  // 在候选中按关键词和性别匹配
  let bestMatch = null;
  let bestScore = -1;
  
  for (const voice of candidates) {
    let score = 0;
    const nameLower = voice.name.toLowerCase();
    
    // 关键词匹配
    for (const keyword of keywords) {
      if (nameLower.includes(keyword.toLowerCase())) {
        score += 10;
      }
    }
    
    // 性别匹配（通过常见名称推断）
    const isFemale = /female|woman|girl|samantha|victoria|kate|karen|zira|susan|hazel|jenny|aria|sonia|natasha/i.test(nameLower);
    const isMale = /male|man|boy|alex|david|daniel|fred|tom|james|mark|guy|oliver|ryan|lee|william/i.test(nameLower);
    
    if (gender === 'Female' && isFemale) score += 5;
    if (gender === 'Male' && isMale) score += 5;
    if (gender === 'Female' && isMale) score -= 5;
    if (gender === 'Male' && isFemale) score -= 5;
    
    // 精确语言匹配加分
    if (voice.lang === locale || voice.lang.replace('_', '-') === locale) {
      score += 3;
    }
    
    // 优先选择 "Natural" 或 "Neural" 音色
    if (/natural|neural|online/i.test(nameLower)) {
      score += 2;
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = voice;
    }
  }
  
  console.log(`TTS: Voice "${voiceKey}" matched to "${bestMatch?.name}" (score: ${bestScore})`);
  return bestMatch;
}

/**
 * 设置当前音色
 */
export function setVoice(voiceKey) {
  if (AVAILABLE_VOICES[voiceKey]) {
    currentVoice = voiceKey;
    localStorage.setItem('tts_voice', voiceKey);
    return true;
  }
  return false;
}

/**
 * 获取当前音色
 */
export function getCurrentVoice() {
  return currentVoice;
}

/**
 * 获取可用音色列表（带系统支持状态）
 */
export async function getAvailableVoices() {
  await initVoices();
  
  const result = {};
  for (const [key, config] of Object.entries(AVAILABLE_VOICES)) {
    const systemVoice = findBestVoice(key);
    result[key] = {
      ...config,
      supported: !!systemVoice,
      systemVoiceName: systemVoice?.name || null
    };
  }
  
  return result;
}

/**
 * 播放语音
 * @param {string} text 要播放的文本
 * @param {object} options 选项 { voice, rate, pitch, volume, onEnd, onError }
 * @returns {object} 控制对象 { stop, pause, resume }
 */
export function speak(text, options = {}) {
  if (!text || !window.speechSynthesis) {
    console.warn('TTS: Not available or text is empty');
    return null;
  }
  
  // 取消当前播放
  window.speechSynthesis.cancel();
  
  const {
    voice = currentVoice,
    rate = 1,
    pitch = 1,
    volume = 1,
    onEnd,
    onError
  } = options;
  
  const utterance = new SpeechSynthesisUtterance(text);
  
  // 设置音色
  const voiceConfig = AVAILABLE_VOICES[voice];
  const systemVoice = findBestVoice(voice);
  
  if (systemVoice) {
    utterance.voice = systemVoice;
    utterance.lang = systemVoice.lang;
    console.log(`TTS: Speaking with voice "${systemVoice.name}" (${systemVoice.lang})`);
  } else if (voiceConfig) {
    utterance.lang = voiceConfig.locale;
    console.log(`TTS: No exact match, using lang "${voiceConfig.locale}"`);
  } else {
    utterance.lang = 'en-US';
    console.log('TTS: Using default lang "en-US"');
  }
  
  utterance.rate = rate;
  utterance.pitch = pitch;
  utterance.volume = volume;
  
  // 事件处理
  if (onEnd) {
    utterance.onend = onEnd;
  }
  if (onError) {
    utterance.onerror = onError;
  }
  
  // 播放
  window.speechSynthesis.speak(utterance);
  
  return {
    stop: () => window.speechSynthesis.cancel(),
    pause: () => window.speechSynthesis.pause(),
    resume: () => window.speechSynthesis.resume(),
    utterance
  };
}

/**
 * 停止所有语音播放
 */
export function stopSpeaking() {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/**
 * 检查是否正在播放
 */
export function isSpeaking() {
  return window.speechSynthesis?.speaking || false;
}

/**
 * 列出所有系统音色（调试用）
 */
export function listSystemVoices() {
  const voices = getSystemVoices();
  console.log('=== System Voices ===');
  voices.forEach((v, i) => {
    console.log(`${i + 1}. ${v.name} (${v.lang}) ${v.localService ? '[Local]' : '[Remote]'}`);
  });
  return voices;
}

export default {
  speak,
  stopSpeaking,
  isSpeaking,
  setVoice,
  getCurrentVoice,
  getAvailableVoices,
  initVoices,
  listSystemVoices,
  AVAILABLE_VOICES,
  DEFAULT_VOICE
};
